/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React, { useEffect, useState } from 'react';

import { useSplitPromptSequence } from '@/hooks/useSplitPromptSequence';

import SearchWithNavigation from '../../ux-common/src/components/common/search-with-navigation';
import { PromptSequenceIcons } from '../icons/icons';
import { SplitViewPanel } from './split-view-panel';

type SplitPanelProps = {
  onSplit?: () => void;
  onCloseSplit?: () => void;
  initialSearchTerm?: string;
  initialCurrentIndex?: number;
};

export const SplitPanel = ({
  onSplit,
  onCloseSplit,
  initialSearchTerm = '',
  initialCurrentIndex = 0,
}: SplitPanelProps) => {
  const [term, setTerm] = useState('');
  const [matches, setMatches] = useState<HTMLElement[]>([]);
  const [currentValue, setCurrentValue] = useState(0);

  const { splitPayload, setSplitPayload } = useSplitPromptSequence();

  // Initialize the search term and current index when the component mounts
  useEffect(() => {
    if (initialSearchTerm) {
      setTerm(initialSearchTerm);
    }
    if (initialCurrentIndex >= 0) {
      setCurrentValue(initialCurrentIndex);
    }
  }, [initialSearchTerm, initialCurrentIndex]);

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setTerm(e.target.value);
  }

  function handleClearSearch() {
    setTerm('');
  }

  function prevMatch() {
    setCurrentValue((prev) => Math.max(prev - 1, 0));
  }

  function nextMatch() {
    setCurrentValue((next) => Math.min(next + 1, matches.length - 1));
  }

  return (
    <div className="h-[672px] w-1/2 shrink-0 overflow-y-auto border-l border-[#40565A]">
      <div className="sticky top-0 z-10 h-12 flex-none border-b border-[#0A343C] bg-card-00 px-4 py-2">
        <div className="flex justify-between">
          {/* Split view search functionality */}
          <SearchWithNavigation
            value={term}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            className="w-[250px]"
            inputClassName="pr-20"
            matches={matches}
            currentIndex={currentValue}
            onPreviousMatch={prevMatch}
            onNextMatch={nextMatch}
            disabled={initialSearchTerm !== ''}
          />
          <button
            className="flex h-6 w-6  items-center justify-center rounded p-0 hover:bg-[#257C9D1F] focus:bg-[#257C9D1F] active:bg-[#257C9D29] disabled:bg-transparent"
            onClick={() => {
              setSplitPayload(null);
              onSplit?.();
              onCloseSplit?.();
            }}
          >
            {PromptSequenceIcons.CloseIcon('h-4 w-4')}
          </button>
        </div>
      </div>
      <SplitViewPanel
        payload={splitPayload}
        searchTerm={term}
        currentMatchIndex={currentValue}
        onMatchesChange={setMatches}
      />
    </div>
  );
};
